import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';
import logoblue from '../assets/logo.png';
import quickchainMockup from '../assets/quickchain_mockup.png';
import ContactModal from '../components/ContactModal';

/* ─── Intersection Observer Hook for scroll animations ─── */
function useScrollReveal() {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { el.classList.add('revealed'); } },
            { threshold: 0.12 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return ref;
}

/* ─── Reusable animated section wrapper ─── */
function Reveal({ children, className = '', delay = 0, tag: Tag = 'div' }) {
    const ref = useScrollReveal();
    return (
        <Tag ref={ref} className={`reveal ${className}`} style={{ transitionDelay: `${delay}ms` }}>
            {children}
        </Tag>
    );
}

/* ─────────────────────────  Main Component  ───────────────────────── */
const LandingPage = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const openModal = useCallback((e) => { e.preventDefault(); setModalOpen(true); }, []);
    const closeModal = useCallback(() => setModalOpen(false), []);
    const [navOpen, setNavOpen] = useState(false);
    const closeNav = useCallback(() => setNavOpen(false), []);

    return (
        <div className="lp-root">

            {/* ── NAV ── */}
            <header className="lp-nav">
                <div className="lp-container lp-nav__inner">
                    <Link to="/" className="lp-nav__brand" onClick={closeNav}>
                        <img src={logoblue} alt="Iron Ridge Cyber" className="lp-nav__logo" />
                        <span className="lp-nav__wordmark">
                            Iron Ridge Cyber
                            <em>QuickChain</em>
                        </span>
                    </Link>
                    <nav className="lp-nav__links">
                        <Link to="/" className="lp-nav__link lp-nav__link--active">Home</Link>
                        <Link to="/about" className="lp-nav__link">About</Link>
                        <a href="#pricing" className="lp-nav__link">Pricing</a>
                        <button onClick={openModal} className="lp-btn lp-btn--sm lp-btn--primary">Get Started</button>
                    </nav>
                    <button
                        className={`lp-hamburger${navOpen ? ' lp-hamburger--open' : ''}`}
                        onClick={() => setNavOpen(o => !o)}
                        aria-label="Toggle navigation"
                    >
                        <span /><span /><span />
                    </button>
                </div>
                {/* Mobile drawer */}
                <div className={`lp-mobile-nav${navOpen ? ' lp-mobile-nav--open' : ''}`}>
                    <Link to="/" className="lp-mobile-nav__link" onClick={closeNav}>Home</Link>
                    <Link to="/about" className="lp-mobile-nav__link" onClick={closeNav}>About</Link>
                    <a href="#pricing" className="lp-mobile-nav__link" onClick={closeNav}>Pricing</a>
                    <button onClick={() => { closeNav(); openModal(); }} className="lp-btn lp-btn--primary" style={{ marginTop: '8px' }}>Get Started</button>
                </div>
            </header>

            {/* ══════════════════════════════════════════════════════
                1. HERO
            ══════════════════════════════════════════════════════ */}
            <section className="lp-hero">
                <div className="lp-container lp-hero__inner">
                    <div className="lp-hero__copy">
                        <Reveal>
                            <div className="lp-eyebrow">You've Built Something Secure. Now Prove It.</div>
                            <h1 className="lp-hero__headline">
                                Generate Auditor-Ready SBOMs and Dependency Risk Reports in Minutes.
                            </h1>
                        </Reveal>
                        <Reveal delay={100}>
                            <p className="lp-hero__sub">
                                Built for SaaS companies moving upmarket and facing enterprise security reviews.
                            </p>
                            <p className="lp-hero__support">
                                Enterprise buyers don't take your word for it anymore. They send a security questionnaire,
                                request a software bill of materials, and wait. If you can't respond in days — not weeks —
                                deals stall, urgency fades, and contracts get re-evaluated.
                            </p>
                        </Reveal>
                        <Reveal delay={200}>
                            <div className="lp-hero__actions">
                                <button onClick={openModal} className="lp-btn lp-btn--primary lp-btn--lg">Generate Your First SBOM</button>
                                <button onClick={openModal} className="lp-btn lp-btn--ghost lp-btn--lg">View Sample Audit Report</button>
                            </div>
                            <div className="lp-trust-badges">
                                <span className="lp-trust-badge"><span className="lp-trust-dot" />Read-only access</span>
                                <span className="lp-trust-badge"><span className="lp-trust-dot" />No source code stored</span>
                                <span className="lp-trust-badge"><span className="lp-trust-dot" />SPDX &amp; CycloneDX output</span>
                            </div>
                        </Reveal>
                    </div>
                    <Reveal delay={150} className="lp-hero__visual">
                        <div className="lp-mockup-frame">
                            <div className="lp-mockup-bar">
                                <span /><span /><span />
                            </div>
                            <img
                                src={quickchainMockup}
                                alt="QuickChain dashboard showing SBOM generation and vulnerability summary"
                                className="lp-mockup-img"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ══════════════════════════════════════════════════════
                2. THE PROBLEM
            ══════════════════════════════════════════════════════ */}
            <section className="lp-section lp-section--alt" id="problem">
                <div className="lp-container">
                    <Reveal>
                        <div className="lp-section-label">The Problem</div>
                        <h2 className="lp-section-h2">Secure Isn't Enough Anymore. You Have to Prove It.</h2>
                    </Reveal>

                    <div className="lp-problem-grid">
                        <Reveal delay={0} className="lp-problem-steps">
                            <div className="lp-step">
                                <div className="lp-step__num">01</div>
                                <div className="lp-step__body">
                                    <h3>Software supply chains are a known attack surface</h3>
                                    <p>The average data breach now costs <strong>$4.45 million</strong>. Enterprise security teams have read the headlines — SolarWinds, Log4j, XZ Utils. They know that third-party software dependencies are where modern attacks hide, and they're not signing contracts without proof.</p>
                                </div>
                            </div>
                            <div className="lp-step">
                                <div className="lp-step__num">02</div>
                                <div className="lp-step__body">
                                    <h3>Enterprise buyers demand documented evidence</h3>
                                    <p>Before approving a $50k–$500k contract, their security team requires a full inventory of every piece of software your product uses, a vulnerability assessment, and evidence that known risks have been addressed. This isn't optional anymore — it's standard procurement.</p>
                                </div>
                            </div>
                            <div className="lp-step">
                                <div className="lp-step__num">03</div>
                                <div className="lp-step__body">
                                    <h3>Your engineering team gets pulled off product work</h3>
                                    <p>The request lands on developers who have never produced a formal security audit document. They spend days — sometimes weeks — manually enumerating dependencies and formatting reports that may or may not satisfy the auditor on the other end.</p>
                                </div>
                            </div>
                            <div className="lp-step">
                                <div className="lp-step__num">04</div>
                                <div className="lp-step__body">
                                    <h3>The deal stalls. Momentum dies.</h3>
                                    <p>A 60–90 day delay on a $100k contract costs more than this tool costs in three years. By the time the documentation is ready, the buyer's urgency has cooled and your sales team is starting over.</p>
                                </div>
                            </div>
                        </Reveal>

                        <Reveal delay={100}>
                            <div className="lp-problem-callout">
                                <p className="lp-problem-callout__text">
                                    Being secure and being able to <em>prove</em> you're secure are two completely different problems.
                                </p>
                                <p className="lp-problem-callout__sub">
                                    Most SaaS companies only discover this difference when a deal is already on the line.
                                    By then, the clock is ticking.
                                </p>
                                <p className="lp-problem-callout__sub" style={{ marginTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '1rem' }}>
                                    A 60-day delay on a single $100k contract costs more than QuickChain costs in <strong>3 years</strong>.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ── CTA STRIP #1 ── */}
            <div className="lp-cta-strip">
                <span className="lp-cta-strip__text">Don't let documentation slow your next deal.</span>
                <div className="lp-cta-strip__actions">
                    <button onClick={openModal} className="lp-btn lp-btn--primary">Generate Your First SBOM</button>
                    <button onClick={openModal} className="lp-btn lp-btn--ghost">View Sample Report</button>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════
                3. DIFFERENTIATION
            ══════════════════════════════════════════════════════ */}
            <section className="lp-section" id="differentiation">
                <div className="lp-container">
                    <Reveal>
                        <div className="lp-section-label">Positioning</div>
                        <h2 className="lp-section-h2">Not Another Developer Scanner</h2>
                        <p className="lp-section-desc">
                            Developer scanners tell your team what's wrong. QuickChain tells your buyer's security team everything they need to approve the deal.
                        </p>
                    </Reveal>

                    <Reveal delay={100} className="lp-compare-wrap">
                        <div className="lp-compare">
                            <div className="lp-compare__header">
                                <div className="lp-compare__col-label lp-compare__col-label--neutral">Developer Scanners</div>
                                <div className="lp-compare__col-label lp-compare__col-label--accent">Iron Ridge QuickChain</div>
                            </div>
                            {[
                                ['Tells your engineers what\'s vulnerable', 'Packages complete audit-ready documentation'],
                                ['Output only engineers can read', 'Built for procurement workflows'],
                                ['Requires a developer to interpret', 'Executive-ready PDF and structured exports'],
                                ['Lists problems with no \'so what\'', 'VEX documents with exploitability analysis'],
                                ['Goes stale after each release', 'CI/CD-integrated — always current'],
                                ['No context on actual risk level', 'AI-powered CVE mapping against the NVD'],
                                ['Generates files procurement rarely accepts', 'SPDX 2.3 & CycloneDX 1.5 — the formats enterprise buyers require'],
                            ].map(([left, right], i) => (
                                <div className="lp-compare__row" key={i}>
                                    <div className="lp-compare__cell lp-compare__cell--left">{left}</div>
                                    <div className="lp-compare__cell lp-compare__cell--right">
                                        <span className="lp-compare__badge">✓</span> {right}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ══════════════════════════════════════════════════════
                4. HOW IT WORKS
            ══════════════════════════════════════════════════════ */}
            <section className="lp-section lp-section--alt" id="how-it-works">
                <div className="lp-container">
                    <Reveal>
                        <div className="lp-section-label">Workflow</div>
                        <h2 className="lp-section-h2">How QuickChain Works</h2>
                        <p className="lp-section-desc">Three steps from repository to auditor-ready package.</p>
                    </Reveal>

                    <div className="lp-steps-grid">
                        {[
                            {
                                step: '1',
                                title: 'Connect in 10 Minutes',
                                desc: 'Give QuickChain read-only access to your repository via GitHub or GitLab. No code is stored. No source files are transmitted. Your engineering team can review and revoke the connection at any time.',
                                tags: ['GitHub', 'GitLab', 'Read-only'],
                            },
                            {
                                step: '2',
                                title: 'QuickChain Does the Heavy Lifting',
                                desc: 'QuickChain maps every dependency in your codebase, cross-references known vulnerabilities, assesses actual risk using AI, and packages everything into the formats enterprise security teams require. No ongoing engineering effort required.',
                                tags: ['SBOM (SPDX 2.3)', 'SBOM (CycloneDX 1.5)', 'VEX Document', 'NVD Mapping', 'Executive Overview'],
                            },
                            {
                                step: '3',
                                title: 'Send It That Same Day',
                                desc: 'Download a complete, professionally formatted audit package and send it to your buyer\'s security team. Everything is in the format they require — nothing for your team to reformat or explain.',
                                tags: ['PDF', 'JSON', 'XML'],
                            },
                        ].map(({ step, title, desc, tags }, i) => (
                            <Reveal key={i} delay={i * 100} className="lp-how-card">
                                <div className="lp-how-card__step">{step}</div>
                                <h3 className="lp-how-card__title">{title}</h3>
                                <p className="lp-how-card__desc">{desc}</p>
                                <div className="lp-how-card__tags">
                                    {tags.map(t => <span className="lp-tag" key={t}>{t}</span>)}
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ══════════════════════════════════════════════════════
                CI/CD INTEGRATION
            ══════════════════════════════════════════════════════ */}
            <section className="lp-section lp-section--alt" id="cicd">
                <div className="lp-container">
                    <Reveal>
                        <div className="lp-section-label">Set It and Forget It</div>
                        <h2 className="lp-section-h2">Set It Up Once. Never Scramble Again.</h2>
                        <p className="lp-section-desc">
                            Most teams spend days preparing security documentation every time a review arrives.
                            QuickChain keeps it current automatically — whether you think about it or not.
                        </p>
                    </Reveal>

                    <div className="lp-cicd-grid">
                        <Reveal delay={0} className="lp-cicd-block">
                            <div className="lp-cicd-block__icon">↺</div>
                            <h3>Updates Every Time You Ship</h3>
                            <p>
                                Every time your team releases a new version of your product, QuickChain automatically
                                regenerates your security documentation. Your audit package is always current —
                                no one on your team needs to remember to update it.
                            </p>
                        </Reveal>
                        <Reveal delay={80} className="lp-cicd-block">
                            <div className="lp-cicd-block__icon">∆</div>
                            <h3>Answers Questions Before They're Asked</h3>
                            <p>
                                Enterprise buyers increasingly want to know: “Is this documentation current as of today?”
                                and “What changed in your software since your last contract?”
                                You'll have both answers on file, already formatted and ready to send.
                            </p>
                        </Reveal>
                        <Reveal delay={160} className="lp-cicd-block">
                            <div className="lp-cicd-block__icon">⚡</div>
                            <h3>Same-Day Response to Any Security Request</h3>
                            <p>
                                When a major deal asks for your security documentation on a Friday afternoon,
                                the answer is a download link — not a two-week project and an engineering sprint.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ── CTA STRIP #2 ── */}
            <div className="lp-cta-strip">
                <span className="lp-cta-strip__text">Ready to see what the output looks like?</span>
                <div className="lp-cta-strip__actions">
                    <button onClick={openModal} className="lp-btn lp-btn--primary">Start Your 14-Day Evaluation</button>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════
                7. PRICING
            ══════════════════════════════════════════════════════ */}
            <section className="lp-section" id="pricing">
                <div className="lp-container">
                    <Reveal>
                        <div className="lp-section-label">Pricing</div>
                        <h2 className="lp-section-h2">Simple, Transparent Pricing</h2>
                        <p className="lp-section-desc">
                            One price. No quote required.
                        </p>
                    </Reveal>

                    <Reveal delay={50} className="lp-pricing-roi">
                        <p className="lp-pricing-roi__label">The math is simple</p>
                        <div className="lp-roi-grid">
                            <div className="lp-roi-stat lp-roi-stat--cost">
                                <div className="lp-roi-stat__amount">~$5,000</div>
                                <div className="lp-roi-stat__title">One engineering week</div>
                                <div className="lp-roi-stat__desc">The real cost of pulling a developer off product work to manually produce security documentation for a single procurement review.</div>
                            </div>
                            <div className="lp-roi-stat lp-roi-stat--cost">
                                <div className="lp-roi-stat__amount">$100k+</div>
                                <div className="lp-roi-stat__title">One delayed deal</div>
                                <div className="lp-roi-stat__desc">The cost of a 60-day sales cycle extension on a single enterprise contract — in lost momentum, cooled urgency, and reconsidered budgets.</div>
                            </div>
                            <div className="lp-roi-stat lp-roi-stat--value">
                                <div className="lp-roi-stat__amount">$3,588</div>
                                <div className="lp-roi-stat__title">QuickChain for a full year</div>
                                <div className="lp-roi-stat__desc">Preventing either scenario above — even once — pays for QuickChain many times over. Most customers recover the cost on their first review.</div>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={100} className="lp-pricing-card">
                        <div className="lp-pricing-card__price">
                            <span className="lp-pricing-card__amount">$299</span>
                            <span className="lp-pricing-card__period">/ month / repository</span>
                        </div>
                        <div className="lp-pricing-card__divider" />
                        <ul className="lp-pricing-card__features">
                            {[
                                '14-day evaluation — no commitment required',
                                'No long-term contract',
                                'SPDX 2.3 and CycloneDX 1.5 SBOM generation',
                                'VEX document with exploitability analysis',
                                'AI-powered CVE mapping against the NVD',
                                'Executive risk overview report',
                                'CI/CD integration via GitHub push',
                                'Direct onboarding and first-audit review support',
                            ].map(f => (
                                <li key={f}>
                                    <span className="lp-check" />
                                    {f}
                                </li>
                            ))}
                        </ul>
                        <button onClick={openModal} className="lp-btn lp-btn--primary lp-btn--lg lp-pricing-card__cta">
                            Start Your 14-Day Evaluation
                        </button>
                        <p className="lp-pricing-card__note">
                            Generate your first package. Have a security-savvy person review it.
                            Ask whether it would pass a real enterprise procurement review without revision.
                            If not, cancel — you owe nothing.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* ══════════════════════════════════════════════════════
                8. FAQ
            ══════════════════════════════════════════════════════ */}
            <section className="lp-section lp-section--alt" id="faq">
                <div className="lp-container">
                    <Reveal>
                        <div className="lp-section-label">FAQ</div>
                        <h2 className="lp-section-h2">Common Questions</h2>
                    </Reveal>
                    <div className="lp-faq-grid">
                        {[
                            {
                                q: 'Will this actually satisfy our enterprise buyer\'s security team?',
                                a: 'Yes. QuickChain generates SPDX 2.3 and CycloneDX 1.5 SBOMs — the two formats explicitly required by Executive Order 14028 and referenced in most enterprise security questionnaires. These are the formats buyers ask for, not a proprietary alternative.'
                            },
                            {
                                q: 'What if they come back with more questions?',
                                a: 'We work with you through the first review. If a procurement team flags the format or asks for additional detail, we address it directly. Enterprise security requirements vary — that\'s exactly why we include direct onboarding support in every plan.'
                            },
                            {
                                q: 'How fast can we be up and running?',
                                a: 'Connecting your repository takes under 10 minutes. Your first full documentation package — SBOM, vulnerability assessment, and executive summary — is ready within a few hours of connecting. Most customers send their first audit package the same day they sign up.'
                            },
                            {
                                q: 'Is it safe to connect this to our codebase?',
                                a: 'QuickChain only reads your dependency declarations — the files that list what software packages your product uses. It never accesses your actual source code, stores files, or transmits anything beyond package metadata. Your engineering team can review and revoke access at any time.'
                            },
                            {
                                q: 'Do we need to change anything in how we currently work?',
                                a: 'No. QuickChain works alongside whatever security tools you already use — it doesn\'t replace them. It adds the procurement documentation layer that existing tools don\'t produce. No changes to your workflow, your pipeline, or your security stack are required to get started.'
                            },
                            {
                                q: 'What if it\'s not the right fit?',
                                a: 'Cancel anytime. The 14-day evaluation requires no credit card commitment. If it doesn\'t do what we say it does, you walk away owing nothing. We\'re confident enough in the output to let the first package speak for itself.'
                            },
                        ].map(({ q, a }, i) => (
                            <Reveal key={i} delay={i * 50} className="lp-faq-item">
                                <h3 className="lp-faq-item__q">{q}</h3>
                                <p className="lp-faq-item__a">{a}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ══════════════════════════════════════════════════════
                9. FINAL CTA
            ══════════════════════════════════════════════════════ */}
            <section className="lp-cta-section" id="cta">
                <div className="lp-container lp-cta-inner">
                    <Reveal>
                        <h2 className="lp-cta-h2">The Next Enterprise Review Is Coming.<br />Be Ready Before It Arrives.</h2>
                    </Reveal>
                    <Reveal delay={100}>
                        <button onClick={openModal} className="lp-btn lp-btn--primary lp-btn--lg lp-btn--inverted">
                            Start Your Free Evaluation
                        </button>
                    </Reveal>
                    <Reveal delay={200}>
                        <p className="lp-cta-sub">
                            14-day trial. No contract. Connect your repository today and have a complete,
                            auditor-ready package by end of day.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* ── FOOTER ── */}
            <footer className="lp-footer">
                <div className="lp-container lp-footer__inner">
                    <div className="lp-footer__brand">
                        <img src={logoblue} alt="Iron Ridge Cyber" className="lp-nav__logo" />
                        <span>Iron Ridge Cyber</span>
                    </div>
                    <div className="lp-footer__meta">
                        <span>© 2026 Iron Ridge Cyber Inc. All rights reserved.</span>
                        <span className="lp-footer__sep">·</span>
                        <span>Legal</span>
                        <span className="lp-footer__sep">·</span>
                        <span>Privacy Policy</span>
                    </div>
                </div>
            </footer>

            <ContactModal isOpen={modalOpen} onClose={closeModal} />

        </div>
    );
};

export default LandingPage;
